// Photos remain visible without JavaScript. The standalone site needs no hydration.
export function initLandingMotion(home: Element | null) {
  if (!home) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  const photos = Array.from(home.querySelectorAll<HTMLElement>('[data-wall-photo]'));
  const images = Array.from(home.querySelectorAll<HTMLImageElement>('img[data-memory-image]'));
  const revealTargets = Array.from(home.querySelectorAll<HTMLElement>('[data-reveal]'));
  const navLinks = Array.from(home.querySelectorAll<HTMLAnchorElement>('.home-nav-about'));
  const sectionTargets = navLinks.map((link) => {
    const href = link.getAttribute('href') || '';
    return href.startsWith('#') ? home.querySelector(href) : null;
  });
  const header = home.querySelector('.home-header');
  const deadline = performance.now() + 1800;
  let disposed = false;
  let finished =
    reducedMotion.matches ||
    Boolean(connection?.saveData) ||
    /(^|-)2g$/.test(connection?.effectiveType || '') ||
    document.hidden;
  let wallObserver: IntersectionObserver | undefined;
  let revealObserver: IntersectionObserver | undefined;
  const cleanup: (() => void)[] = [];
  // Without the readiness class these blocks keep their natural, readable state.
  const showReveals = () => {
    for (const target of revealTargets) target.classList.add('is-revealed');
    revealObserver?.disconnect();
  };
  const settle = () => {
    finished = true;
    for (const photo of photos) photo.classList.remove('is-arriving');
    wallObserver?.disconnect();
  };
  const onVisibility = () => {
    if (document.hidden) settle();
  };
  const onPreference = () => {
    if (!reducedMotion.matches) return;
    settle();
    showReveals();
  };
  // Defer setup so StrictMode's rehearsal cannot consume the one-shot animation.
  const frame = requestAnimationFrame(() => {
    if (disposed) return;
    for (const img of images) {
      const figure = img.closest<HTMLElement>('.home-memory');
      const onReady = () => {
        if (disposed || !figure) return;
        if (!img.naturalWidth) {
          figure.classList.add('is-failed');
          return;
        }
        if (!figure.hasAttribute('data-wall-photo') || figure.dataset.arrived) return;
        figure.dataset.arrived = 'true';
        if (!finished && performance.now() < deadline) figure.classList.add('is-arriving');
      };
      const onError = () => {
        figure?.classList.add('is-failed');
      };
      const onEnd = () => {
        figure?.classList.remove('is-arriving');
      };
      img.addEventListener('load', onReady, { once: true });
      img.addEventListener('error', onError, { once: true });
      figure?.addEventListener('animationend', onEnd);
      cleanup.push(() => {
        img.removeEventListener('load', onReady);
        img.removeEventListener('error', onError);
        figure?.removeEventListener('animationend', onEnd);
      });
      if (img.complete) onReady();
    }
    if (!finished && 'IntersectionObserver' in window) {
      const wall = home.querySelector('[data-photo-wall]');
      wallObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => !entry.isIntersecting)) settle();
      });
      if (wall) wallObserver.observe(wall);
    }
    // Scroll reveal: one pass per block, never replayed.
    if (finished || !('IntersectionObserver' in window)) {
      showReveals();
    } else {
      home.classList.add('home-motion-ready');
      revealObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-revealed');
            revealObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
      );
      for (const target of revealTargets) revealObserver.observe(target);
    }
  });

  // Header state and current-section highlight. rAF throttled, transform/opacity only.
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      header?.classList.toggle('is-scrolled', window.scrollY > 40);
      let current = -1;
      for (let i = 0; i < sectionTargets.length; i += 1) {
        const rect = sectionTargets[i]?.getBoundingClientRect();
        if (rect && rect.top <= 160) current = i;
      }
      navLinks.forEach((link, index) => link.classList.toggle('is-active', index === current));
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', settle);
  reducedMotion.addEventListener('change', onPreference);
  return () => {
    disposed = true;
    cancelAnimationFrame(frame);
    settle();
    showReveals();
    cleanup.forEach((fn) => fn());
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', settle);
    window.removeEventListener('scroll', onScroll);
    reducedMotion.removeEventListener('change', onPreference);
  };
}
